/**
 * DealSubHeader — sub-header content for an ahoy_deal slideout.
 *
 * Layout: `Stage badge | Owner: badge   Company badge`. Registered on
 * `ENTITY_TYPE_REGISTRY.ahoy_deal.components.subHeader`. Consumers should
 * go through `SubHeaderSection`.
 */

import type { ReactNode } from 'react';
import { useMemo } from 'react';

import type { CrmObject } from '@/types/crm';

import { EntityBadgeById } from '../../components/badges/EntityBadgeById';
import { useAssociations } from '../../hooks/useAssociations';
import { usePipelineStages } from '../../hooks/usePipelineStages';
import { getStageEntityId } from '../../utils/propertyHelpers';
import { SubHeaderLayout } from './sub-header-layout';

export function DealSubHeader({ object }: { object: CrmObject }) {
  const orgId = object.org_id;

  // Pre-filter to only the associations this sub-header reads (owner +
  // company). useAssociations eagerly fetches target entities for every
  // association passed in, so handing it the full list would trigger
  // network calls for unrelated targets that we never render here.
  const associations = useMemo(
    () =>
      (object.associations ?? []).filter(
        a =>
          a.association_type_name === 'ahoy_deal_to_company' ||
          (a.association_type_name.includes('_owner') &&
            a.association_type_name.includes('_to_owner'))
      ),
    [object.associations]
  );

  const { associations: resolvedAssociations } = useAssociations(
    orgId,
    associations
  );

  const { stageTypeNames } = usePipelineStages(orgId, 'ahoy_deal');
  const stageEntityId = getStageEntityId(object, stageTypeNames);

  const stageNode: ReactNode = stageEntityId ? (
    <EntityBadgeById
      entityId={stageEntityId}
      orgId={orgId}
      variant='xs'
      clickBehavior='page'
    />
  ) : undefined;

  // Owner badge
  const ownerAssoc = resolvedAssociations.find(
    a =>
      a.association_type_name.includes('_owner') &&
      a.association_type_name.includes('_to_owner')
  );

  const ownerNode = ownerAssoc?.target_entity_id ? (
    <span className='inline-flex items-center gap-1'>
      <span className='text-fg-tertiary'>Owner:</span>
      <EntityBadgeById
        entityId={ownerAssoc.target_entity_id}
        orgId={orgId}
        variant='xs'
      />
    </span>
  ) : undefined;

  // Associated company badge
  const companyAssoc = resolvedAssociations.find(
    a => a.association_type_name === 'ahoy_deal_to_company'
  );

  const companyNode = companyAssoc?.target_entity_id ? (
    <EntityBadgeById
      entityId={companyAssoc.target_entity_id}
      orgId={orgId}
      variant='xs'
    />
  ) : undefined;

  return (
    <SubHeaderLayout
      primary={stageNode}
      secondary={ownerNode}
      tertiary={companyNode}
    />
  );
}
